import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CalendarDaysIcon, PlusIcon, PaperAirplaneIcon, TrashIcon, ArrowPathIcon,
  BanknotesIcon, DocumentTextIcon, CheckCircleIcon, XCircleIcon, ClockIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useExchangeRate } from '../contexts/ExchangeRateContext';
import {
  getStores, getAccountsPayable,
  getPlanAvailabilitySnapshot, listPaymentPlans, getPaymentPlan, createPaymentPlan,
  updatePaymentPlan, sendPaymentPlan, deletePaymentPlan, setPlanItemStatus, payPlanItem,
  getPlanItemReceiptUrl, downloadPaymentDocument, openPaymentDocumentFile, openExpenseDocumentFile,
} from '../api/client';
import { formatDateTimeVE } from '../utils/formatters';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt$ = (v) => `$ ${Number(v || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtBs = (v) => `Bs ${Number(v || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayVE = () => new Date(Date.now() - 4 * 3600 * 1000).toISOString().slice(0, 10);

const BANKS = [
  { key: 'banesco', label: 'Banesco' }, { key: 'bdv', label: 'BDV' }, { key: 'bnc', label: 'BNC' },
  { key: 'banplus', label: 'Banplus' }, { key: 'provincial', label: 'Provincial' },
  { key: 'efectivo_usd', label: 'Efectivo USD' }, { key: 'efectivo_bs', label: 'Efectivo Bs' },
  { key: 'zelle', label: 'Zelle' },
];
const METHODS = [
  { value: 'transferencia', label: 'Transferencia' }, { value: 'pago_movil', label: 'Pago móvil' },
  { value: 'efectivo', label: 'Efectivo' }, { value: 'zelle', label: 'Zelle' }, { value: 'tarjeta', label: 'Tarjeta' },
];
const CATEGORIES = [
  ['servicios', 'Servicios (luz, agua, internet…)'], ['operativo', 'Operativo'], ['mantenimiento', 'Mantenimiento'],
  ['personal', 'Personal'], ['marketing', 'Marketing'], ['seguros', 'Seguros'], ['sueldos_directivos', 'Sueldos directivos'],
  ['creditos_bancarios', 'Créditos bancarios'], ['comisiones_bancarias', 'Comisiones bancarias'], ['otros', 'Otros'],
];
const bankLabel = (k) => BANKS.find(b => b.key === k)?.label || (k ? k.toUpperCase() : '—');

const STATUS = {
  draft:  { label: 'Borrador', cls: 'bg-gray-100 text-gray-700' },
  sent:   { label: 'Enviado',  cls: 'bg-blue-100 text-blue-800' },
  closed: { label: 'Cerrado',  cls: 'bg-gray-200 text-gray-600' },
};
const ITEM_STATUS = {
  planificado: { label: 'Planificado', cls: 'bg-amber-50 text-amber-800 border-amber-200', Icon: ClockIcon },
  pagado:      { label: 'Pagado',      cls: 'bg-green-50 text-green-800 border-green-200', Icon: CheckCircleIcon },
  no_pagado:   { label: 'No pagado',   cls: 'bg-red-50 text-red-800 border-red-200', Icon: XCircleIcon },
};

const Modal = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
    <div className={`bg-white rounded-xl shadow-xl w-full ${wide ? 'max-w-4xl' : 'max-w-lg'} max-h-[92vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
        <h3 className="font-bold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

// ─── Editor de plan ──────────────────────────────────────────────────────────

function PlanEditor({ plan, stores, onSaved, onCancel }) {
  const { exchangeRate } = useExchangeRate();
  const rate = typeof exchangeRate === 'number' ? exchangeRate : (exchangeRate?.usd_to_ves_rate || 0);

  const [date, setDate] = useState(plan?.date || todayVE());
  const [availability, setAvailability] = useState(plan?.availability || []);
  const [items, setItems] = useState(plan?.items || []);
  const [notes, setNotes] = useState(plan?.notes || '');
  const [manualVes, setManualVes] = useState(plan?.manual_available_ves ?? '');
  const [manualUsd, setManualUsd] = useState(plan?.manual_available_usd ?? '');
  const [showDetail, setShowDetail] = useState(false);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Añadir factura
  const [showInv, setShowInv] = useState(false);
  const [invStore, setInvStore] = useState('');
  const [invSupplier, setInvSupplier] = useState('');
  const [apData, setApData] = useState(null);
  const [loadingAp, setLoadingAp] = useState(false);
  const [invBank, setInvBank] = useState('');
  const [invMethod, setInvMethod] = useState('transferencia');
  const [invChecked, setInvChecked] = useState(new Set());
  const apCache = useRef({});

  // Añadir gasto
  const [showExp, setShowExp] = useState(false);
  const [exp, setExp] = useState({ concept: '', category: 'servicios', store_id: '', amount_usd: '', amount_ves: '', currency: 'USD', bank: '', method: 'transferencia', notes: '' });

  const loadAvailability = useCallback(async () => {
    setLoadingAvail(true);
    try {
      const data = await getPlanAvailabilitySnapshot();
      setAvailability(data.availability || []);
    } catch { setError('No se pudo cargar la disponibilidad'); }
    finally { setLoadingAvail(false); }
  }, []);

  useEffect(() => { if (!plan) loadAvailability(); }, [plan, loadAvailability]);

  useEffect(() => {
    if (!showInv || !invStore) { setApData(null); return; }
    if (apCache.current[invStore]) { setApData(apCache.current[invStore]); return; }
    let cancelled = false; setLoadingAp(true);
    getAccountsPayable({ store_id: invStore }).then(d => { if (!cancelled) { apCache.current[invStore] = d; setApData(d); } })
      .catch(() => {}).finally(() => { if (!cancelled) setLoadingAp(false); });
    return () => { cancelled = true; };
  }, [showInv, invStore]);

  const suppliers = apData?.[invStore]?.suppliers ? Object.entries(apData[invStore].suppliers).sort((a, b) => b[1].total_debt - a[1].total_debt) : [];
  const invoices = (apData?.[invStore]?.suppliers?.[invSupplier]?.invoices || []).filter(inv => !items.some(i => i.invoice_id === inv.id));

  const addInvoices = () => {
    if (!invBank) { setError('Selecciona el banco de donde saldrá el pago'); return; }
    const sel = invoices.filter(inv => invChecked.has(inv.id));
    if (!sel.length) return;
    const storeName = stores.find(s => s.id === invStore)?.name || '';
    setItems(prev => [...prev, ...sel.map(inv => ({
      id: crypto.randomUUID(), type: 'factura', invoice_id: inv.id, invoice_number: inv.invoice_number,
      supplier: invSupplier, store_id: invStore, store_name: storeName,
      amount_usd: Number(inv.remaining_debt || inv.total_amount_usd || 0),
      amount_ves: rate ? Number((inv.remaining_debt || 0) * rate) : 0,
      currency: 'USD', bank: invBank, method: invMethod, notes: '', status: 'planificado', due_date: inv.due_date,
    }))]);
    setInvChecked(new Set()); setShowInv(false); setError('');
  };

  const addExpense = () => {
    if (!exp.concept.trim()) { setError('Escribe el concepto del gasto'); return; }
    const usd = parseFloat(exp.amount_usd || 0), ves = parseFloat(exp.amount_ves || 0);
    if (usd <= 0 && ves <= 0) { setError('Indica el monto del gasto'); return; }
    if (!exp.bank) { setError('Selecciona el banco de donde saldrá el pago'); return; }
    setItems(prev => [...prev, {
      id: crypto.randomUUID(), type: 'gasto', concept: exp.concept.trim(), category: exp.category,
      store_id: exp.store_id || null, store_name: stores.find(s => s.id === exp.store_id)?.name || '',
      amount_usd: usd > 0 ? usd : (rate ? ves / rate : 0), amount_ves: ves > 0 ? ves : (rate ? usd * rate : 0),
      currency: exp.currency, bank: exp.bank, method: exp.method, notes: exp.notes, status: 'planificado',
    }]);
    setExp({ concept: '', category: 'servicios', store_id: '', amount_usd: '', amount_ves: '', currency: 'USD', bank: '', method: 'transferencia', notes: '' });
    setShowExp(false); setError('');
  };

  const removeItem = (id) => setItems(prev => prev.filter(i => i.id !== id));
  const updateItem = (id, patch) => setItems(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i));

  // Totales: un renglón consume USD si sale de una cuenta/medio en dólares; si no, Bs.
  const isUsdBank = (b) => ['efectivo_usd', 'zelle'].includes(b) || (b || '').endsWith('_usd');
  const autoVesT = availability.filter(a => a.currency !== 'USD').reduce((t, a) => t + Number(a.balance || 0), 0);
  const autoUsdT = availability.filter(a => a.currency === 'USD').reduce((t, a) => t + Number(a.balance || 0), 0);
  const availVes = manualVes === '' || manualVes === null ? autoVesT : Number(manualVes);
  const availUsd = manualUsd === '' || manualUsd === null ? autoUsdT : Number(manualUsd);
  const plannedVes = items.filter(i => !isUsdBank(i.bank)).reduce((t, i) => t + Number(i.amount_ves || 0), 0);
  const plannedUsd = items.filter(i => isUsdBank(i.bank)).reduce((t, i) => t + Number(i.amount_usd || 0), 0);
  const totalUsd = items.reduce((t, i) => t + Number(i.amount_usd || 0), 0);
  const totalVes = items.reduce((t, i) => t + Number(i.amount_ves || 0), 0);
  const byBankInfo = {};
  items.forEach(i => { const k = i.bank || 'sin_banco'; byBankInfo[k] = (byBankInfo[k] || 0) + Number(i.amount_usd || 0); });

  const save = async (andSend) => {
    if (!items.length) { setError('Agrega al menos un renglón'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        date, notes,
        manual_available_ves: manualVes === '' || manualVes === null ? null : Number(manualVes),
        manual_available_usd: manualUsd === '' || manualUsd === null ? null : Number(manualUsd),
        availability: availability.map(a => ({ bank_key: a.bank_key, bank_name: a.bank_name, store_id: a.store_id || null, store_name: a.store_name || null, currency: a.currency, balance: Number(a.balance || 0), source: a.source || 'manual', period_end: a.period_end || null })),
        items: items.map(i => ({
          id: i.id, type: i.type, invoice_id: i.invoice_id || null, concept: i.concept || null, category: i.category || null,
          store_id: i.store_id || null, amount_usd: Number(i.amount_usd || 0), amount_ves: Number(i.amount_ves || 0),
          currency: i.currency || 'USD', bank: i.bank || '', method: i.method || 'transferencia', notes: i.notes || '',
        })),
      };
      let saved = plan ? await updatePaymentPlan(plan.id, payload) : await createPaymentPlan(payload);
      if (andSend) saved = await sendPaymentPlan(saved.id);
      onSaved(saved);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Fecha del plan</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="form-input" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <label className="block text-xs font-semibold text-gray-500 mb-1">Nota para el dueño (opcional)</label>
          <input value={notes} onChange={e => setNotes(e.target.value)} className="form-input" placeholder="Ej: hoy no entra Banplus, se paga mañana" />
        </div>
      </div>

      {/* Disponibilidad: dos totales, Bs y USD. El dinero se mezcla entre tiendas. */}
      {(() => {
        const autoVes = availability.filter(a => a.currency !== 'USD').reduce((t, a) => t + Number(a.balance || 0), 0);
        const autoUsd = availability.filter(a => a.currency === 'USD').reduce((t, a) => t + Number(a.balance || 0), 0);
        return (
          <div className="bg-white border border-gray-200 rounded-xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2"><BanknotesIcon className="w-5 h-5 text-teal-600" /> Disponibilidad de hoy</h3>
              <button onClick={loadAvailability} disabled={loadingAvail} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                <ArrowPathIcon className={`w-3.5 h-3.5 ${loadingAvail ? 'animate-spin' : ''}`} /> Recargar estados de cuenta
              </button>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[['Bs', autoVes, manualVes, setManualVes, fmtBs], ['USD', autoUsd, manualUsd, setManualUsd, fmt$]].map(([cur, auto, manual, setManual, f]) => (
                <div key={cur} className="border border-gray-200 rounded-lg p-3">
                  <p className="text-xs font-bold text-gray-500 uppercase">{cur}</p>
                  <p className="text-sm text-gray-600 mt-1">Según estados de cuenta <span className="font-mono font-semibold text-gray-900 float-right">{f(auto)}</span></p>
                  <label className="block text-xs font-semibold text-gray-500 mt-3 mb-1">Disponible real hoy (ajusta si es distinto)</label>
                  <input type="number" step="0.01" value={manual} placeholder={auto.toFixed(2)} onChange={e => setManual(e.target.value)} className="form-input font-mono text-right text-lg font-bold" />
                  <p className="text-[11px] text-gray-400 mt-1">{manual === '' || manual === null ? 'Se usará el de los estados de cuenta' : 'Ajustado a mano'}</p>
                </div>
              ))}
            </div>
            <div className="px-4 pb-3">
              <button onClick={() => setShowDetail(v => !v)} className="text-xs text-gray-500 hover:text-gray-700">{showDetail ? '▾' : '▸'} Detalle por cuenta ({availability.length})</button>
              {showDetail && (
                <div className="mt-2 max-h-56 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-50 text-xs">
                  {availability.map((a, idx) => (
                    <div key={idx} className="flex justify-between px-3 py-1"><span className="text-gray-700">{a.store_name || 'General'} · {a.bank_name}</span><span className="font-mono text-gray-800">{a.currency === 'USD' ? fmt$(a.balance) : fmtBs(a.balance)} <span className="text-gray-400">al {a.period_end || '—'}</span></span></div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Renglones */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-wrap gap-2">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2"><DocumentTextIcon className="w-5 h-5 text-indigo-600" /> Qué se va a pagar</h3>
          <div className="flex gap-2">
            <button onClick={() => setShowInv(true)} className="btn-primary text-sm flex items-center gap-1"><PlusIcon className="w-4 h-4" /> Factura</button>
            <button onClick={() => setShowExp(true)} className="btn-outline text-sm flex items-center gap-1"><PlusIcon className="w-4 h-4" /> Gasto vario</button>
          </div>
        </div>
        {items.length === 0 ? (
          <p className="p-6 text-sm text-gray-400 text-center">Agrega las facturas y gastos que se pagarán hoy.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-400 uppercase border-b border-gray-100">
                <tr><th className="px-3 py-2 text-left">Tipo</th><th className="px-3 py-2 text-left">Detalle</th><th className="px-3 py-2 text-left">Tienda</th>
                  <th className="px-3 py-2 text-right">USD</th><th className="px-3 py-2 text-right">Bs</th><th className="px-3 py-2 text-left">Banco</th><th className="px-3 py-2 text-left">Método</th><th></th></tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map(i => (
                  <tr key={i.id}>
                    <td className="px-3 py-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${i.type === 'factura' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>{i.type === 'factura' ? 'FACTURA' : 'GASTO'}</span></td>
                    <td className="px-3 py-2">
                      {i.type === 'factura'
                        ? <><span className="font-medium text-gray-900">{i.supplier}</span> <span className="text-gray-500 font-mono text-xs">#{i.invoice_number}</span></>
                        : <><span className="font-medium text-gray-900">{i.concept}</span> <span className="text-gray-400 text-xs">[{CATEGORIES.find(c => c[0] === i.category)?.[1] || i.category}]</span></>}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">{i.store_name || '—'}</td>
                    <td className="px-3 py-2 text-right"><input type="number" step="0.01" value={i.amount_usd} onChange={e => updateItem(i.id, { amount_usd: e.target.value, amount_ves: rate ? (Number(e.target.value) * rate).toFixed(2) : i.amount_ves })} className="form-input w-28 text-right font-mono py-1" /></td>
                    <td className="px-3 py-2 text-right"><input type="number" step="0.01" value={Number(i.amount_ves || 0).toFixed(2)} onChange={e => updateItem(i.id, { amount_ves: e.target.value })} className="form-input w-32 text-right font-mono py-1" /></td>
                    <td className="px-3 py-2"><select value={i.bank} onChange={e => updateItem(i.id, { bank: e.target.value })} className="form-select py-1 text-xs">{BANKS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}</select></td>
                    <td className="px-3 py-2"><select value={i.method} onChange={e => updateItem(i.id, { method: e.target.value })} className="form-select py-1 text-xs">{METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}</select></td>
                    <td className="px-2 py-2"><button onClick={() => removeItem(i.id)} className="text-gray-300 hover:text-red-600" title="Quitar"><TrashIcon className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Resumen: disponible vs planificado, por moneda */}
      {items.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="font-semibold text-gray-800 mb-3">Disponible vs planificado</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[['Bolívares', availVes, plannedVes, fmtBs], ['Dólares', availUsd, plannedUsd, fmt$]].map(([label, avail, planned, f]) => {
              const over = planned > avail + 0.01; const rem = avail - planned;
              return (
                <div key={label} className={`rounded-lg border px-4 py-3 ${over ? 'border-red-300 bg-red-50' : 'border-green-200 bg-green-50/40'}`}>
                  <p className="text-xs font-bold text-gray-600 uppercase">{label}</p>
                  <p className="text-sm text-gray-600">Disponible <span className="font-mono float-right">{f(avail)}</span></p>
                  <p className="text-sm text-gray-600">Planificado <span className="font-mono float-right">{f(planned)}</span></p>
                  <p className={`text-base font-bold ${over ? 'text-red-700' : 'text-green-700'}`}>{over ? 'Excede por' : 'Queda'} <span className="font-mono float-right">{f(Math.abs(rem))}</span></p>
                </div>
              );
            })}
          </div>
          <p className="text-sm text-gray-700 mt-3">Total del plan: <strong>{fmt$(totalUsd)}</strong> · <strong>{fmtBs(totalVes)}</strong> · {items.filter(i => i.type === 'factura').length} facturas · {items.filter(i => i.type === 'gasto').length} gastos</p>
          <p className="text-xs text-gray-400 mt-1">Sale de: {Object.entries(byBankInfo).sort((a, b) => b[1] - a[1]).map(([b, v]) => `${bankLabel(b)} ${fmt$(v)}`).join(' · ')}</p>
        </div>
      )}

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2">{error}</div>}

      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="btn-outline">Cancelar</button>
        <button onClick={() => save(false)} disabled={saving} className="btn-outline">{saving ? 'Guardando…' : 'Guardar borrador'}</button>
        <button onClick={() => save(true)} disabled={saving} className="btn-primary flex items-center gap-2"><PaperAirplaneIcon className="w-4 h-4" /> {saving ? 'Enviando…' : 'Enviar plan al dueño'}</button>
      </div>

      {/* Modal factura */}
      {showInv && (
        <Modal title="Agregar facturas al plan" onClose={() => setShowInv(false)} wide>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Tienda</label>
              <select value={invStore} onChange={e => { setInvStore(e.target.value); setInvSupplier(''); setInvChecked(new Set()); }} className="form-select">
                <option value="">Seleccionar</option>{stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Proveedor</label>
              <select value={invSupplier} onChange={e => { setInvSupplier(e.target.value); setInvChecked(new Set()); }} className="form-select" disabled={!invStore || loadingAp}>
                <option value="">{loadingAp ? 'Cargando…' : 'Seleccionar'}</option>{suppliers.map(([name, d]) => <option key={name} value={name}>{name} ({fmt$(d.total_debt)})</option>)}</select></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Banco</label>
              <select value={invBank} onChange={e => setInvBank(e.target.value)} className="form-select"><option value="">Seleccionar</option>{BANKS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}</select></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Método</label>
              <select value={invMethod} onChange={e => setInvMethod(e.target.value)} className="form-select">{METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}</select></div>
          </div>
          {invSupplier && (
            <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto">
              {invoices.length === 0 ? <p className="p-4 text-sm text-gray-400">Sin facturas pendientes (o ya están en el plan).</p> :
                invoices.map(inv => (
                  <label key={inv.id} className="flex items-center gap-3 px-3 py-2 border-b border-gray-50 hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={invChecked.has(inv.id)} onChange={() => setInvChecked(prev => { const n = new Set(prev); n.has(inv.id) ? n.delete(inv.id) : n.add(inv.id); return n; })} />
                    <span className="font-mono text-sm">#{inv.invoice_number}</span>
                    <span className="text-xs text-gray-500">vence {inv.due_date || '—'}</span>
                    <span className="ml-auto font-semibold text-blue-700">{fmt$(inv.remaining_debt)}</span>
                  </label>
                ))}
            </div>
          )}
          <div className="flex justify-between items-center mt-4">
            <span className="text-sm text-gray-600">{invChecked.size} seleccionadas · {fmt$(invoices.filter(i => invChecked.has(i.id)).reduce((s, i) => s + Number(i.remaining_debt || 0), 0))}</span>
            <button onClick={addInvoices} disabled={!invChecked.size} className="btn-primary">Agregar al plan</button>
          </div>
        </Modal>
      )}

      {/* Modal gasto */}
      {showExp && (
        <Modal title="Agregar gasto vario al plan" onClose={() => setShowExp(false)}>
          <div className="space-y-3">
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Concepto</label>
              <input value={exp.concept} onChange={e => setExp(x => ({ ...x, concept: e.target.value }))} className="form-input" placeholder="Ej: Corpoelec Farmalife septiembre" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Categoría</label>
                <select value={exp.category} onChange={e => setExp(x => ({ ...x, category: e.target.value }))} className="form-select">{CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Tienda</label>
                <select value={exp.store_id} onChange={e => setExp(x => ({ ...x, store_id: e.target.value }))} className="form-select"><option value="">General</option>{stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Monto USD</label>
                <input type="number" step="0.01" value={exp.amount_usd} onChange={e => setExp(x => ({ ...x, amount_usd: e.target.value, amount_ves: rate && e.target.value ? (Number(e.target.value) * rate).toFixed(2) : x.amount_ves }))} className="form-input" /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Monto Bs</label>
                <input type="number" step="0.01" value={exp.amount_ves} onChange={e => setExp(x => ({ ...x, amount_ves: e.target.value, currency: 'VES' }))} className="form-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Banco</label>
                <select value={exp.bank} onChange={e => setExp(x => ({ ...x, bank: e.target.value }))} className="form-select"><option value="">Seleccionar</option>{BANKS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}</select></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Método</label>
                <select value={exp.method} onChange={e => setExp(x => ({ ...x, method: e.target.value }))} className="form-select">{METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}</select></div>
            </div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Nota</label>
              <input value={exp.notes} onChange={e => setExp(x => ({ ...x, notes: e.target.value }))} className="form-input" /></div>
            <div className="flex justify-end"><button onClick={addExpense} className="btn-primary">Agregar al plan</button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Modal registrar pago de un renglón ──────────────────────────────────────

function PayItemModal({ plan, item, onClose, onPaid }) {
  const { exchangeRate } = useExchangeRate();
  const rate = typeof exchangeRate === 'number' ? exchangeRate : (exchangeRate?.usd_to_ves_rate || 0);
  const [f, setF] = useState({
    payment_date: todayVE(), payment_method: item.method || 'transferencia', bank: item.bank || '',
    payment_reference: '', payment_reference2: '', payment_reference3: '', payment_reference4: '',
    amount_usd: Number(item.amount_usd || 0).toFixed(2), amount_ves: Number(item.amount_ves || 0).toFixed(2), notes: '',
  });
  const [files, setFiles] = useState([]);
  const file = files[0] || null;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!files.length) { setErr('El comprobante PDF es obligatorio'); return; }
    if (!f.payment_reference.trim() && f.payment_method !== 'efectivo') { setErr('Escribe la referencia del pago'); return; }
    setBusy(true); setErr('');
    try {
      const r = await payPlanItem(plan.id, item.id, { ...f, exchange_rate_used: rate || 0 }, files);
      onPaid(r.item);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={item.type === 'factura' ? `Registrar pago · ${item.supplier} #${item.invoice_number}` : `Registrar pago · ${item.concept}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">Fecha de pago</label><input type="date" value={f.payment_date} onChange={e => setF(x => ({ ...x, payment_date: e.target.value }))} className="form-input" /></div>
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">Banco</label><select value={f.bank} onChange={e => setF(x => ({ ...x, bank: e.target.value }))} className="form-select">{BANKS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}</select></div>
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">Método</label><select value={f.payment_method} onChange={e => setF(x => ({ ...x, payment_method: e.target.value }))} className="form-select">{METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}</select></div>
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">Monto USD</label><input type="number" step="0.01" value={f.amount_usd} onChange={e => setF(x => ({ ...x, amount_usd: e.target.value, amount_ves: rate ? (Number(e.target.value) * rate).toFixed(2) : x.amount_ves }))} className="form-input font-mono" /></div>
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">Monto Bs (lo transferido)</label><input type="number" step="0.01" value={f.amount_ves} onChange={e => setF(x => ({ ...x, amount_ves: e.target.value }))} className="form-input font-mono" /></div>
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">Referencia</label><input value={f.payment_reference} onChange={e => setF(x => ({ ...x, payment_reference: e.target.value }))} className="form-input font-mono" placeholder="Nº de la transferencia" /></div>
        </div>
        <details className="text-xs text-gray-500"><summary className="cursor-pointer">Más referencias (si fueron varias transferencias)</summary>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {['payment_reference2', 'payment_reference3', 'payment_reference4'].map(k => <input key={k} value={f[k]} onChange={e => setF(x => ({ ...x, [k]: e.target.value }))} className="form-input font-mono" placeholder="Referencia" />)}
          </div></details>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Comprobante(s) PDF · obligatorio</label>
          <input type="file" accept=".pdf" multiple onChange={e => setFiles(Array.from(e.target.files || []))} className="form-input" />
          {files.length ? <ul className="text-xs text-green-700 mt-1">{files.map((x, i) => <li key={i}>📄 {x.name}</li>)}</ul>
            : <p className="text-xs text-red-600 mt-1">Sin comprobante no se puede registrar. Si fueron varias transferencias, selecciona todos los PDF.</p>}
        </div>
        <div><label className="block text-xs font-semibold text-gray-500 mb-1">Nota</label><input value={f.notes} onChange={e => setF(x => ({ ...x, notes: e.target.value }))} className="form-input" /></div>
        {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</p>}
        <div className="flex justify-end gap-2"><button onClick={onClose} className="btn-outline">Cancelar</button>
          <button onClick={submit} disabled={busy || !file} className="btn-primary">{busy ? 'Registrando…' : '💸 Registrar pago'}</button></div>
      </div>
    </Modal>
  );
}

// ─── Vista de un plan ────────────────────────────────────────────────────────

function PlanView({ plan, onChange, onEdit, onBack }) {
  const { isAdmin } = useAuth();
  const [payItem, setPayItem] = useState(null);
  const [busy, setBusy] = useState(false);
  const t = plan.totals || {};
  const st = STATUS[plan.status] || STATUS.draft;

  const markNotPaid = async (item) => {
    const reason = window.prompt(`¿Por qué no se pagó "${item.type === 'factura' ? item.supplier + ' #' + item.invoice_number : item.concept}"?`);
    if (reason === null) return;
    setBusy(true);
    try { const r = await setPlanItemStatus(plan.id, item.id, 'no_pagado', reason); onChange({ ...plan, items: plan.items.map(i => i.id === item.id ? r.item : i) }); }
    catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  const undoNotPaid = async (item) => {
    setBusy(true);
    try { const r = await setPlanItemStatus(plan.id, item.id, 'planificado'); onChange({ ...plan, items: plan.items.map(i => i.id === item.id ? r.item : i) }); }
    catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  const send = async () => {
    if (!window.confirm('¿Enviar el plan al dueño por Telegram?')) return;
    setBusy(true);
    try { onChange(await sendPaymentPlan(plan.id)); } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('¿Borrar este plan? No se puede deshacer.')) return;
    try { await deletePaymentPlan(plan.id); onBack(true); } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const openDoc = (item, d) => {
    if (item.type === 'factura' && item.payment_id) return openPaymentDocumentFile(item.payment_id, d.file_id);
    if (item.expense_id) return openExpenseDocumentFile(item.expense_id, d.file_id);
  };
  const openReceipt = async (item) => {
    try {
      if (item.type === 'factura' && item.payment_id) {
        const blob = await downloadPaymentDocument(item.payment_id);
        window.open(URL.createObjectURL(blob), '_blank');
      } else if (item.receipt_file_id) {
        const token = localStorage.getItem('farmavida_token');
        const res = await fetch(getPlanItemReceiptUrl(plan.id, item.id), { headers: { Authorization: `Bearer ${token}` } });
        window.open(URL.createObjectURL(await res.blob()), '_blank');
      }
    } catch { alert('No se pudo abrir el comprobante'); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <button onClick={() => onBack(false)} className="text-sm text-blue-600 hover:underline mb-1">← Todos los planes</button>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">{plan.plan_number} <span className={`text-xs px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span></h2>
          <p className="text-sm text-gray-500">{new Date(plan.date + 'T12:00:00').toLocaleDateString('es-VE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })} · armado por {plan.created_by_name}{plan.sent_at && ` · enviado ${formatDateTimeVE(plan.sent_at)}`}</p>
          {plan.notes && <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-1 mt-2 inline-block">📝 {plan.notes}</p>}
        </div>
        <div className="flex gap-2">
          {plan.status === 'draft' && <button onClick={send} disabled={busy} className="btn-primary flex items-center gap-1"><PaperAirplaneIcon className="w-4 h-4" /> Enviar al dueño</button>}
          {plan.status !== 'closed' && <button onClick={onEdit} className="btn-outline">Editar</button>}
          {(t.pagados || 0) === 0 && <button onClick={remove} className="btn-outline text-red-600 border-red-200"><TrashIcon className="w-4 h-4" /></button>}
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3"><p className="text-xs text-gray-500 uppercase font-semibold">Total plan</p><p className="text-lg font-black">{fmt$(t.total_usd)}</p><p className="text-xs text-gray-400">{fmtBs(t.total_ves)}</p></div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"><p className="text-xs text-amber-700 uppercase font-semibold">Por pagar</p><p className="text-lg font-black text-amber-800">{t.pendientes || 0}</p></div>
        <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3"><p className="text-xs text-green-700 uppercase font-semibold">Pagados</p><p className="text-lg font-black text-green-800">{t.pagados || 0}</p></div>
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3"><p className="text-xs text-red-700 uppercase font-semibold">No pagados</p><p className="text-lg font-black text-red-800">{t.no_pagados || 0}</p></div>
      </div>

      {/* Disponible vs planificado */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="font-semibold text-gray-800 mb-3">Disponible vs planificado {t.available_manual && <span className="text-xs font-normal text-amber-700">(disponible ajustado a mano)</span>}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[['Bolívares', t.available_ves, t.planned_ves, t.remaining_ves, t.over_ves, fmtBs, t.available_ves_auto], ['Dólares', t.available_usd, t.planned_usd, t.remaining_usd, t.over_usd, fmt$, t.available_usd_auto]].map(([label, avail, planned, rem, over, f, auto]) => (
            <div key={label} className={`rounded-lg border px-4 py-3 ${over ? 'border-red-300 bg-red-50' : 'border-green-200 bg-green-50/40'}`}>
              <p className="text-xs font-bold text-gray-600 uppercase">{label}</p>
              <p className="text-sm text-gray-600">Disponible <span className="font-mono float-right">{f(avail)}</span></p>
              {t.available_manual && <p className="text-[11px] text-gray-400">según estados de cuenta: {f(auto)}</p>}
              <p className="text-sm text-gray-600">Planificado <span className="font-mono float-right">{f(planned)}</span></p>
              <p className={`text-base font-bold ${over ? 'text-red-700' : 'text-green-700'}`}>{over ? 'Excede por' : 'Queda'} <span className="font-mono float-right">{f(Math.abs(rem || 0))}</span></p>
            </div>
          ))}
        </div>
      </div>

      {/* Renglones */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-400 uppercase border-b border-gray-100">
            <tr><th className="px-3 py-2 text-left">Tipo</th><th className="px-3 py-2 text-left">Detalle</th><th className="px-3 py-2 text-left">Tienda</th><th className="px-3 py-2 text-right">USD</th><th className="px-3 py-2 text-right">Bs</th><th className="px-3 py-2 text-left">Banco</th><th className="px-3 py-2 text-center">Estado</th><th className="px-3 py-2 text-right">Acción</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {plan.items.map(i => { const s = ITEM_STATUS[i.status] || ITEM_STATUS.planificado; const Icon = s.Icon; return (
              <tr key={i.id} className={i.status === 'pagado' ? 'bg-green-50/30' : i.status === 'no_pagado' ? 'bg-red-50/30' : ''}>
                <td className="px-3 py-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${i.type === 'factura' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>{i.type === 'factura' ? 'FACTURA' : 'GASTO'}</span></td>
                <td className="px-3 py-2">
                  {i.type === 'factura' ? <><span className="font-medium">{i.supplier}</span> <span className="font-mono text-xs text-gray-500">#{i.invoice_number}</span></> : <><span className="font-medium">{i.concept}</span> <span className="text-xs text-gray-400">[{i.category}]</span></>}
                  {i.status === 'pagado' && <p className="text-[11px] text-green-700">{i.paid_by_name ? `pagó ${i.paid_by_name}` : 'pagado'}{i.paid_reference ? ` · ref ${i.paid_reference}` : ''}{i.paid_at ? ` · ${formatDateTimeVE(i.paid_at)}` : ''}</p>}
                  {i.status === 'no_pagado' && i.status_notes && <p className="text-[11px] text-red-700">motivo: {i.status_notes}</p>}
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">{i.store_name || '—'}</td>
                <td className="px-3 py-2 text-right font-mono">{fmt$(i.amount_usd)}</td>
                <td className="px-3 py-2 text-right font-mono text-gray-600">{fmtBs(i.amount_ves)}</td>
                <td className="px-3 py-2 text-xs">{bankLabel(i.bank)} <span className="text-gray-400">· {METHODS.find(m => m.value === i.method)?.label || i.method}</span></td>
                <td className="px-3 py-2 text-center"><span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${s.cls}`}><Icon className="w-3.5 h-3.5" />{s.label}</span></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {i.status === 'planificado' && <>
                    <button onClick={() => setPayItem(i)} disabled={busy} className="text-xs px-2 py-1 rounded bg-green-600 hover:bg-green-700 text-white mr-1">Registrar pago</button>
                    <button onClick={() => markNotPaid(i)} disabled={busy} className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700" title="Marcar que no se pagó">No pagado</button>
                  </>}
                  {i.status === 'no_pagado' && <button onClick={() => undoNotPaid(i)} disabled={busy} className="text-xs text-blue-600 hover:underline">volver a planificado</button>}
                  {i.status === 'pagado' && i.documents && i.documents.length > 1
                    ? <span className="inline-flex gap-1">{i.documents.map((d, di) => <button key={d.file_id} onClick={() => openDoc(i, d)} className="text-xs text-blue-600 hover:underline" title={d.filename}>PDF {di + 1}</button>)}</span>
                    : i.status === 'pagado' && (i.has_receipt || i.receipt_file_id) && <button onClick={() => openReceipt(i)} className="text-xs text-blue-600 hover:underline">Ver comprobante</button>}
                </td>
              </tr>); })}
          </tbody>
        </table>
      </div>
      {!isAdmin && <p className="text-xs text-gray-400">Los pagos registrados aquí quedan en Cuentas por Pagar y Gastos como cualquier otro, y el dueño los audita después.</p>}

      {payItem && <PayItemModal plan={plan} item={payItem} onClose={() => setPayItem(null)} onPaid={(it) => { onChange({ ...plan, items: plan.items.map(x => x.id === it.id ? it : x) }); setPayItem(null); }} />}
    </div>
  );
}

// ─── Página principal ────────────────────────────────────────────────────────

export default function PaymentPlanning() {
  const [stores, setStores] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list');   // list | new | view | edit
  const [current, setCurrent] = useState(null);
  const [range, setRange] = useState({ from: new Date(Date.now() - 14 * 86400000).toISOString().slice(0, 10), to: todayVE() });

  const load = useCallback(async () => {
    setLoading(true);
    try { setPlans(await listPaymentPlans({ date_from: range.from, date_to: range.to })); } catch { /* noop */ } finally { setLoading(false); }
  }, [range]);

  useEffect(() => { getStores().then(setStores).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const openPlan = async (p) => { try { setCurrent(await getPaymentPlan(p.id)); setMode('view'); } catch { /* noop */ } };
  const refreshCurrent = async () => { if (current) { try { setCurrent(await getPaymentPlan(current.id)); } catch { /* noop */ } } };

  if (mode === 'new' || mode === 'edit') {
    return (
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2"><CalendarDaysIcon className="w-7 h-7 text-indigo-600" /> {mode === 'new' ? 'Nuevo plan de pagos' : `Editar ${current.plan_number}`}</h1>
        <PlanEditor plan={mode === 'edit' ? current : null} stores={stores}
          onSaved={(saved) => { setCurrent(saved); setMode('view'); load(); }}
          onCancel={() => setMode(current ? 'view' : 'list')} />
      </div>
    );
  }

  if (mode === 'view' && current) {
    return (
      <div className="max-w-6xl mx-auto">
        <PlanView plan={current} onChange={(p) => { setCurrent(p); load(); }} onEdit={() => setMode('edit')}
          onBack={(deleted) => { setMode('list'); setCurrent(null); if (deleted) load(); }} />
        <button onClick={refreshCurrent} className="mt-4 text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"><ArrowPathIcon className="w-3.5 h-3.5" /> actualizar (detecta pagos hechos desde Cuentas por Pagar)</button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><CalendarDaysIcon className="w-7 h-7 text-indigo-600" /> Plan de Pagos del Día</h1>
          <p className="text-sm text-gray-500 mt-1">Disponibilidad por banco y lo que se va a pagar hoy: facturas y gastos varios. Se envía al dueño y se registra qué se pagó.</p>
        </div>
        <button onClick={() => { setCurrent(null); setMode('new'); }} className="btn-primary flex items-center gap-2"><PlusIcon className="w-4 h-4" /> Nuevo plan</button>
      </div>

      <div className="flex gap-3 items-end mb-4">
        <div><label className="block text-xs font-semibold text-gray-500 mb-1">Desde</label><input type="date" value={range.from} onChange={e => setRange(r => ({ ...r, from: e.target.value }))} className="form-input" /></div>
        <div><label className="block text-xs font-semibold text-gray-500 mb-1">Hasta</label><input type="date" value={range.to} onChange={e => setRange(r => ({ ...r, to: e.target.value }))} className="form-input" /></div>
        <button onClick={load} className="btn-outline flex items-center gap-1"><ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar</button>
      </div>

      {loading ? <p className="text-gray-400 text-sm">Cargando…</p> : plans.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
          <CalendarDaysIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay planes en este rango.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {plans.map(p => { const st = STATUS[p.status] || STATUS.draft; const t = p.totals || {}; return (
            <button key={p.id} onClick={() => openPlan(p)} className="w-full text-left bg-white border border-gray-200 rounded-xl px-5 py-4 hover:border-indigo-300 hover:shadow-sm transition">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-bold text-gray-900">{new Date(p.date + 'T12:00:00').toLocaleDateString('es-VE', { weekday: 'long', day: '2-digit', month: 'long' })} <span className="text-gray-400 font-normal text-sm">· {p.plan_number}</span> <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span></p>
                  <p className="text-xs text-gray-500">por {p.created_by_name} · {t.facturas || 0} facturas · {t.gastos || 0} gastos{(t.over_ves || t.over_usd) && <span className="text-red-600 font-semibold"> · excede disponibilidad</span>}</p>
                </div>
                <div className="text-right">
                  <p className="font-black text-gray-900">{fmt$(t.total_usd)}</p>
                  <p className="text-xs"><span className="text-green-700">{t.pagados || 0} pagados</span> · <span className="text-amber-700">{t.pendientes || 0} por pagar</span>{t.no_pagados > 0 && <> · <span className="text-red-700">{t.no_pagados} no pagados</span></>}</p>
                </div>
              </div>
            </button>); })}
        </div>
      )}
    </div>
  );
}
